import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../store';
import { refreshToken as refreshTokenAction, logout as logoutAction } from '../store/authSlice';
import { refreshTokenAPI, logout as logoutAPI } from '../services/auth';
import jwtDecode from 'jwt-decode';
import { useCallback, useMemo } from 'react';

interface DecodedToken {
    permissions: string[];
    exp: number;
    role: string;
}

export const useAuth = () => {
    const dispatch = useDispatch();
    const auth = useSelector((state: RootState) => state.auth);

    const isAuthenticated = useMemo(() => {
        return !!auth.token && (auth.tokenExpiration && Date.now() < auth.tokenExpiration);
    }, [auth.token, auth.tokenExpiration]);

    const decodeToken = useCallback((token: string): DecodedToken | null => {
        try {
            return jwtDecode<DecodedToken>(token);
        } catch (error) {
            console.error('Failed to decode token:', error);
            return null;
        }
    }, []);



    const hasPermission = useCallback((requiredPermissions: string[]): boolean => {
        if (!auth.token) return false;
        const decoded = decodeToken(auth.token);
        if (!decoded) return false;
        return requiredPermissions.some(permission => decoded.permissions.includes(permission));
    }, [auth.token, decodeToken]);

    const refreshAuthToken = useCallback(async (): Promise<boolean> => {
        if (!auth.refreshToken) {
            console.error('No refresh token available');
            dispatch(logoutAction());
            return false;
        }

        try {
            const response = await refreshTokenAPI(auth.refreshToken);
            dispatch(refreshTokenAction({
                access_token: response.access_token,
                refresh_token: response.refresh_token,
                expires_in: response.expires_in
            }));
            return true;
        } catch (error) {
            console.error('Failed to refresh token:', error);
            dispatch(logoutAction());
            return false;
        }
    }, [auth.refreshToken, dispatch]);

    const logout = useCallback(async () => {
        try {
            if (auth.token) {
                await logoutAPI();
            }
        } catch (error) {
            console.error('Error during logout:', error);
        } finally {
            dispatch(logoutAction());
        }
    }, [auth.token, dispatch]);

    const checkAuth = useCallback(async () => {
        if (!isAuthenticated) {
            await refreshAuthToken();
        }
    }, [isAuthenticated, refreshAuthToken]);

    const tokenData = useMemo(() => auth.token ? decodeToken(auth.token) : null, [auth.token, decodeToken]);
    const user = useMemo(() => auth.user, [auth.user]);
    const permissions = useMemo(() => tokenData?.permissions || [], [tokenData]);
    const role = useMemo(() => tokenData?.role || '', [tokenData]);

    return {
        isAuthenticated,
        user,
        token: auth.token,
        refreshToken: auth.refreshToken,
        userPermissions: permissions,
        role,
        hasPermission,
        refreshAuthToken,
        logout,
        checkAuth,
    };
};
